import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/client';
import type { SetList, Song } from '../types';

export interface SetlistPerformanceState {
  status: 'loading' | 'error' | 'empty' | 'ready';
  error: string | null;
  setList: SetList | null;
  songs: Song[];
  currentSongIndex: number;
}

export interface SetlistPerformanceActions {
  goToNextSong: () => void;
  goToPreviousSong: () => void;
  goToSong: (index: number) => void;
}

/**
 * Custom hook that manages the setlist performance session state.
 *
 * Fetches the setlist by ID, resolves all songs in parallel, filters out
 * invalid song IDs, and exposes navigation controls for moving between songs.
 */
export function useSetlistPerformance(
  setListId: string | undefined,
): { state: SetlistPerformanceState; actions: SetlistPerformanceActions } {
  const [state, setState] = useState<SetlistPerformanceState>({
    status: 'loading',
    error: null,
    setList: null,
    songs: [],
    currentSongIndex: 0,
  });

  useEffect(() => {
    if (!setListId) {
      setState({ status: 'error', error: 'No setlist ID provided', setList: null, songs: [], currentSongIndex: 0 });
      return;
    }

    let cancelled = false;

    async function load() {
      setState({ status: 'loading', error: null, setList: null, songs: [], currentSongIndex: 0 });

      try {
        const setList = await apiClient.getSetList(setListId!);

        if (cancelled) return;

        if (!setList.songIds || setList.songIds.length === 0) {
          setState({ status: 'empty', error: null, setList, songs: [], currentSongIndex: 0 });
          return;
        }

        const results = await Promise.allSettled(
          setList.songIds.map((id) => apiClient.getSong(id)),
        );

        if (cancelled) return;

        const songs: Song[] = [];
        for (const result of results) {
          if (result.status === 'fulfilled') {
            songs.push(result.value);
          }
        }

        if (songs.length === 0) {
          setState({ status: 'empty', error: null, setList, songs: [], currentSongIndex: 0 });
        } else {
          setState({ status: 'ready', error: null, setList, songs, currentSongIndex: 0 });
        }
      } catch {
        if (!cancelled) {
          setState({ status: 'error', error: 'Failed to load setlist', setList: null, songs: [], currentSongIndex: 0 });
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [setListId]);

  const goToNextSong = useCallback(() => {
    setState((prev) => {
      if (prev.currentSongIndex >= prev.songs.length - 1) return prev;
      return { ...prev, currentSongIndex: prev.currentSongIndex + 1 };
    });
  }, []);

  const goToPreviousSong = useCallback(() => {
    setState((prev) => {
      if (prev.currentSongIndex <= 0) return prev;
      return { ...prev, currentSongIndex: prev.currentSongIndex - 1 };
    });
  }, []);

  const goToSong = useCallback((index: number) => {
    setState((prev) => {
      const clamped = Math.max(0, Math.min(index, prev.songs.length - 1));
      return { ...prev, currentSongIndex: clamped };
    });
  }, []);

  return { state, actions: { goToNextSong, goToPreviousSong, goToSong } };
}
